// src/components/billing/RecordPaymentDialog.js
//
// Saisie d'un encaissement manuel (espèces, virement, terminal Bancontact…)
// sur UNE échéance d'une inscription. Utilisé depuis Subscriptions.js (onglet
// « À encaisser ») et depuis la fiche de suivi d'un élève.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Alert, Typography, CircularProgress,
} from '@mui/material';
import {
  MANUAL_METHOD_LABELS, dayKey, formatDay, money, planLabel,
} from '../../utils/billing';
import { recordManualPayment } from '../../utils/billingActions';

export default function RecordPaymentDialog({ open, target, onClose, onSaved }) {
  // target = { reg, installment, groupLabel }
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote]     = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!open || !target) return;
    const inst = target.installment;
    setAmount(inst ? String(inst.remaining || inst.amount || '') : '');
    setPaidAt(dayKey(new Date()));
    setMethod('cash');
    setNote('');
    setError('');
  }, [open, target]);

  if (!target) return null;
  const { reg, installment } = target;
  const isMollie = reg.paymentMode === 'mollie';
  const parsedAmount = Number(String(amount).replace(',', '.'));
  const valid = Number.isFinite(parsedAmount) && parsedAmount > 0 && !!paidAt;

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    setError('');
    try {
      await recordManualPayment(reg.id, {
        installmentNumber: installment?.number || 1,
        amount: parsedAmount,
        method,
        paidAt,
        note,
      });
      onSaved?.(`Encaissement de ${money(parsedAmount)} enregistré`);
    } catch (e) {
      console.error('Erreur encaissement:', e);
      setError("L'encaissement n'a pas pu être enregistré (droits Firestore ?).");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#7C3AED', color: '#fff' }}>Encaisser un paiement</DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Typography sx={{ fontWeight: 600 }}>{target.groupLabel || ''}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {planLabel(reg)}
          {installment && installment.number > 0 && (
            <> · échéance {installment.number} du {formatDay(installment.dueDate)} ({money(installment.amount)})</>
          )}
          {installment?.partial && <> · déjà reçu {money(installment.paidAmount)}</>}
        </Typography>

        {isMollie && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Abonnement prélevé par Mollie : cet encaissement n'arrête pas le prélèvement
            automatique. Vérifie dans Mollie qu'il ne sera pas prélevé une seconde fois.
          </Alert>
        )}

        <TextField
          label="Montant reçu (€)" fullWidth size="small" sx={{ mb: 2 }}
          value={amount} onChange={(e) => setAmount(e.target.value)}
          inputProps={{ inputMode: 'decimal' }}
        />
        <TextField
          label="Date de réception" type="date" fullWidth size="small" sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
          value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
        />
        <TextField
          select label="Moyen de paiement" fullWidth size="small" sx={{ mb: 2 }}
          value={method} onChange={(e) => setMethod(e.target.value)}
        >
          {Object.entries(MANUAL_METHOD_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField
          label="Note (facultatif)" fullWidth size="small"
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="ex. communication du virement"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={!valid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
